import { useCallback, useEffect, useMemo, useRef, type MutableRefObject, type RefObject } from 'react';

const SMOOTH_SCROLL_DURATION_MS = 400;
/**
 * How long a bottom jump keeps re-pinning. One frame isn't enough: avatars,
 * cards and web fonts land after the first paint and grow the timeline, which
 * would otherwise leave the view parked just above the newest turn.
 */
const BOTTOM_PIN_DURATION_MS = 700;
/** Scroll drift (px) treated as the user taking over, ending the pin. */
const BOTTOM_PIN_RELEASE_SLOP_PX = 4;

export interface UseBizyChatScrollControllerOptions {
	/**
	 * Whether the idle blank slate (BIZY-683) is active. Mirrored into a ref
	 * so rAF callbacks scheduled in the same commit that activated it (e.g.
	 * the continuous-init bottom jump) see the fresh value.
	 */
	isBlankSlate: boolean;
}

export interface BizyChatScrollController {
	/** The scrollable timeline container. */
	containerRef: RefObject<HTMLDivElement>;
	/**
	 * "Scroll to the newest user turn when the next messages render lands."
	 * Exposed as a raw ref because useBizyExternalOpenAutoSend consumes it
	 * directly; prefer requestScrollToLatestMessage() elsewhere.
	 */
	shouldScrollRef: MutableRefObject<boolean>;
	requestScrollToLatestMessage: () => void;
	/**
	 * Smooth scroll with a controlled speed (plain `scrollTo({behavior:
	 * 'smooth'})` doesn't let us set the duration). Takes a target GETTER,
	 * re-read every frame: layout can shift while the animation runs (late
	 * timeline hydration, font reflow, async card content), and a target
	 * computed once at animation start then lands wrong. The final frame
	 * snaps exactly to the freshly-read target.
	 */
	animateScrollTo: (getScrollTarget: () => number) => void;
	cancelSmoothScroll: () => void;
	isSmoothScrolling: () => boolean;
	/**
	 * Instant bottom jump on the next frame (continuous-init load / finished
	 * loading a conversation). Skipped while the blank slate is active — its
	 * divider positioning is authoritative, and an instant bottom-jump would
	 * race (and could out-write) it. Checked at rAF time via the mirrored ref
	 * because the blank slate may activate in the same commit as the caller's
	 * effect.
	 */
	jumpToBottom: () => void;
}

/**
 * Owns the chat timeline's scroll mechanics: the shared smooth-scroll
 * animation and instant bottom jumps. The chat view composes these into its
 * intent-level effects (send-scroll, init jump, blank-slate anchoring).
 */
export function useBizyChatScrollController({ isBlankSlate }: UseBizyChatScrollControllerOptions): BizyChatScrollController {
	const containerRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	const isSmoothScrollingRef = useRef(false);
	const smoothScrollFrameRef = useRef<number | null>(null);
	const jumpFrameRef = useRef<number | null>(null);
	// Tears down the in-flight bottom pin (frame + its listeners). Held in a ref
	// so a second jumpToBottom — init and the late greeter both fire one — fully
	// retires the previous pin instead of leaving its listeners attached to
	// cancel the new pin's frame.
	const stopBottomPinRef = useRef<(() => void) | null>(null);

	const isBlankSlateRef = useRef(isBlankSlate);
	isBlankSlateRef.current = isBlankSlate;

	const cancelSmoothScroll = useCallback(() => {
		if (smoothScrollFrameRef.current !== null) {
			cancelAnimationFrame(smoothScrollFrameRef.current);
			smoothScrollFrameRef.current = null;
		}
		isSmoothScrollingRef.current = false;
	}, []);

	// Unmount: stop any in-flight animation or pending jump (including the
	// bottom pin's scroll listeners).
	useEffect(() => {
		return () => {
			cancelSmoothScroll();
			stopBottomPinRef.current?.();
		};
	}, [cancelSmoothScroll]);

	const animateScrollTo = useCallback(
		(getScrollTarget: () => number) => {
			const container = containerRef.current;
			if (!container) return;
			cancelSmoothScroll();
			const startPos = container.scrollTop;
			let startTime: number | null = null;
			isSmoothScrollingRef.current = true;

			const step = (timestamp: number) => {
				if (!startTime) startTime = timestamp;
				const progress = Math.min((timestamp - startTime) / SMOOTH_SCROLL_DURATION_MS, 1);
				const eased = 1 - (1 - progress) ** 3;
				const scrollTarget = getScrollTarget();
				container.scrollTop = progress < 1 ? startPos + (scrollTarget - startPos) * eased : scrollTarget;
				if (progress < 1) {
					smoothScrollFrameRef.current = requestAnimationFrame(step);
				} else {
					smoothScrollFrameRef.current = null;
					isSmoothScrollingRef.current = false;
				}
			};
			smoothScrollFrameRef.current = requestAnimationFrame(step);
		},
		[cancelSmoothScroll]
	);

	const jumpToBottom = useCallback(() => {
		// Retire any pin already running before starting a new one.
		stopBottomPinRef.current?.();

		const container = containerRef.current;
		const startedAt = performance.now();
		// Tracks where we last parked so a user scrolling mid-pin hands control back.
		let pinnedTop = -1;

		const stop = () => {
			// Guard the shared frame ref: only the pin that is still current may
			// cancel it, otherwise a stale listener would kill a newer pin.
			if (stopBottomPinRef.current === stop) {
				if (jumpFrameRef.current !== null) cancelAnimationFrame(jumpFrameRef.current);
				jumpFrameRef.current = null;
				stopBottomPinRef.current = null;
			}
			container?.removeEventListener('wheel', stop);
			container?.removeEventListener('touchstart', stop);
		};
		stopBottomPinRef.current = stop;

		const pinToBottom = () => {
			jumpFrameRef.current = null;
			if (isBlankSlateRef.current) return stop();

			const el = containerRef.current;
			if (!el) return stop();
			if (pinnedTop >= 0 && Math.abs(el.scrollTop - pinnedTop) > BOTTOM_PIN_RELEASE_SLOP_PX) return stop();

			const bottom = el.scrollHeight - el.clientHeight;
			if (el.scrollTop !== bottom) el.scrollTo({ top: bottom, behavior: 'instant' });
			pinnedTop = el.scrollTop;

			if (performance.now() - startedAt < BOTTOM_PIN_DURATION_MS) {
				jumpFrameRef.current = requestAnimationFrame(pinToBottom);
			} else {
				stop();
			}
		};

		container?.addEventListener('wheel', stop, { passive: true });
		container?.addEventListener('touchstart', stop, { passive: true });
		jumpFrameRef.current = requestAnimationFrame(pinToBottom);
	}, []);

	const requestScrollToLatestMessage = useCallback(() => {
		shouldScrollRef.current = true;
	}, []);

	const isSmoothScrolling = useCallback(() => isSmoothScrollingRef.current, []);

	return useMemo(
		() => ({
			containerRef,
			shouldScrollRef,
			requestScrollToLatestMessage,
			animateScrollTo,
			cancelSmoothScroll,
			isSmoothScrolling,
			jumpToBottom,
		}),
		[requestScrollToLatestMessage, animateScrollTo, cancelSmoothScroll, isSmoothScrolling, jumpToBottom]
	);
}
